import { EmailEntityId, PhoneNumberE164, SentMessageId } from "@sp-crm/core";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import React, { useMemo, useState } from "react";
import { useFeature } from "store/selectors/hooks";
import {
    AdvancedSearchEntityType,
    GetUserMailboxMessagesForEntityQuery,
    useGetConversationFeedForEntityQuery,
    useGetUserMailboxMessagesForEntityQuery,
} from "../../generated/graphql";
import { ErrorMessage } from "../ui/error-message";
import { Spinner } from "../ui/spinner";
import { MessageRow } from "./message-row";
import { ReceivedMessageDetail } from "./received-message-detail";
import { SentMessageDetail } from "./sent-detail";
import { mergeAndSortMessages, UnifiedMessage } from "./unified-message";

type ReceivedMessage =
    GetUserMailboxMessagesForEntityQuery["getUserMailboxMessagesForEntity"][number];

interface UnifiedMessageFeedProps {
    entityId: EmailEntityId;
    entityType: AdvancedSearchEntityType;
}

export const UnifiedMessageFeed: React.FC<UnifiedMessageFeedProps> = props => {
    const { entityId, entityType } = props;

    const mailboxSyncEnabled = useFeature("mailboxSync");

    const conversationQuery = useGetConversationFeedForEntityQuery({ entityId });
    const mailboxQuery = useGetUserMailboxMessagesForEntityQuery(
        { id: entityId },
        { enabled: mailboxSyncEnabled },
    );

    const [viewingSentId, setViewingSentId] = useState<SentMessageId | null>(null);
    const [viewingReceived, setViewingReceived] = useState<ReceivedMessage | null>(null);
    const [viewingSmsRecipient, setViewingSmsRecipient] =
        useState<PhoneNumberE164 | null>(null);

    const isLoading =
        conversationQuery.isLoading || (mailboxSyncEnabled && mailboxQuery.isLoading);
    const hasError = conversationQuery.isError || mailboxQuery.isError;

    const messages: UnifiedMessage[] = useMemo(() => {
        const receivedMessages = mailboxQuery.data?.getUserMailboxMessagesForEntity ?? [];
        const conversationFeed = conversationQuery.data?.getConversationFeedForEntity;
        return mergeAndSortMessages(receivedMessages, conversationFeed);
    }, [mailboxQuery.data, conversationQuery.data]);

    const handleViewDetail = (message: UnifiedMessage) => {
        if (message.type === "sent" && message.originalSent) {
            setViewingSentId(message.originalSent.id);
        } else if (message.type === "received" && message.originalReceived) {
            setViewingReceived(message.originalReceived);
        } else if (message.type === "sms" && message.originalSmsThread) {
            setViewingSmsRecipient(
                message.originalSmsThread.contact.phoneNumber as PhoneNumberE164,
            );
        }
    };

    const handleCloseSentDetail = () => {
        setViewingSentId(null);
    };

    const handleCloseReceivedDetail = () => {
        setViewingReceived(null);
    };

    const handleCloseSmsPanel = () => {
        setViewingSmsRecipient(null);
    };

    if (isLoading) {
        return <Spinner />;
    }

    if (hasError) {
        return (
            <ErrorMessage component="UnifiedMessageFeed">
                <pre>
                    {JSON.stringify(
                        conversationQuery.error || mailboxQuery.error,
                        null,
                        2,
                    )}
                </pre>
            </ErrorMessage>
        );
    }

    return (
        <div className="flex flex-col max-w-screen-lg">
            {viewingSentId ? (
                <SentMessageDetail
                    messageId={viewingSentId}
                    onDismiss={handleCloseSentDetail}
                />
            ) : null}
            {viewingReceived ? (
                <ReceivedMessageDetail
                    message={viewingReceived}
                    onDismiss={handleCloseReceivedDetail}
                />
            ) : null}
            {viewingSmsRecipient ? (
                <SendSmsPanel
                    isOpen={true}
                    onDismiss={handleCloseSmsPanel}
                    recipient={viewingSmsRecipient}
                />
            ) : null}

            <div className="flow-root">
                <ul role="list">
                    {messages.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            <em>No messages yet</em>
                        </div>
                    ) : (
                        messages.map(message => (
                            <MessageRow
                                key={message.id}
                                message={message}
                                onViewDetail={() => handleViewDetail(message)}
                                perspective={entityType}
                            />
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
};
